<template>
    <div class="chat-forward-window">
        <template v-if="currentScreen === 'agents'">
            <a-tabs tabPosition="left">
                <a-tab-pane key="available-agents" :tab="$t('message.toAvailableAgents')">
                    <div class="search-input">
                        <a-input-search
                            :placeholder="$t('message.searchAgent')"
                            v-model="searchText"
                            @search="onSearch"
                        />
                    </div>

                    <template v-if="filteredAgents.length > 0">
                        <SupsisTable :columns="columns" :rows="filteredAgents" v-bind="tableOptions" />
                    </template>
                    <template v-else>
                        <div class="no-agents-placeholder">
                            <a-icon type="frown" />
                            <p>{{ $t("message.noAgentsAvailableToForward") }}</p>
                        </div>
                    </template>
                </a-tab-pane>
                <a-tab-pane
                    key="available-departments"
                    :tab="$t('message.toAvailableDepartments')"
                    v-if="!isOwneredContact"
                >
                    <div class="search-input">
                        <a-input-search
                            :placeholder="$t('message.searchDepartment')"
                            v-model="departmentSearchText"
                            @search="onDepartmentSearch"
                        />
                    </div>
                    <div class="channel-visibility-info">
                        <div class="info-box">
                            <div class="info-content">
                                <div class="info-icon">
                                    <a-icon type="info-circle" />
                                </div>
                                <div class="info-text">
                                    <div class="info-title">Kanal gizlilik ayarlarınızı kontrol ediniz</div>
                                    <div class="info-description d-flex align-items-center justify-content-between">
                                        <span
                                            >Göremediğiniz departmanlar var ise kanal gizlilik ayarlarını kontrol
                                            ediniz</span
                                        >
                                        <SupsisButton ghost type="primary" @click="goToChannelSettings">
                                            Kanal ayarlarına git
                                        </SupsisButton>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <template v-if="filteredDepartments.length > 0">
                        <SupsisTable :columns="departmentColumns" :rows="filteredDepartments" v-bind="tableOptions" />
                    </template>
                    <template v-else>
                        <div class="no-departments-placeholder">
                            <a-icon type="frown" />
                            <p>{{ $t("message.noDepartmentsAvailableToForward") }}</p>
                        </div>
                    </template>
                </a-tab-pane>
                <a-tab-pane
                    key="both-agent-department"
                    :tab="$t('message.toWithAgentAndDepartment')"
                    v-if="!isOwneredContact"
                >
                    <div class="search-input">
                        <a-input-search
                            :placeholder="$t('message.searchAgentOrDepartment')"
                            v-model="groupedSearchText"
                            @search="onGroupedSearch"
                        />
                    </div>
                    <template v-if="filteredGroupedAgents.length > 0">
                        <div v-for="(group, index) in filteredGroupedAgents" :key="index">
                            <div class="department-header" @click="toggleDepartment(group.departmentId)">
                                <a-icon
                                    :type="isDepartmentExpanded(group.departmentId) ? 'minus-square' : 'plus-square'"
                                />
                                <h3 :style="{ color: getDepartmentColor(group.departmentName) }">
                                    {{ group.departmentName }}
                                </h3>
                            </div>
                            <SupsisTable
                                v-show="isDepartmentExpanded(group.departmentId)"
                                :columns="groupedAgentColumns"
                                :rows="group.agents"
                                v-bind="tableOptions"
                            />
                        </div>
                    </template>
                    <template v-else>
                        <div class="no-agents-placeholder">
                            <a-icon type="frown" />
                            <p>{{ $t("message.noAgentsAvailableToForward") }}</p>
                        </div>
                    </template>
                </a-tab-pane>
                <a-tab-pane
                    key="to-mobile-agents"
                    :tab="$t('message.toMobileAgents')"
                    v-if="false && !isOwneredContact"
                    class="d-none"
                >
                    <div class="search-input">
                        <a-input-search
                            :placeholder="$t('message.searchMobileAgent')"
                            v-model="mobileAgentSearchText"
                            @search="onMobileAgentSearch"
                        />
                    </div>
                    <template v-if="filteredMobileAgents.length > 0">
                        <SupsisTable :columns="columns" :rows="filteredMobileAgents" v-bind="tableOptions" />
                    </template>
                    <template v-else>
                        <div class="no-agents-placeholder">
                            <a-icon type="frown" />
                            <p>{{ $t("message.noMobileAgentsAvailableToForward") }}</p>
                        </div>
                    </template>
                </a-tab-pane>
                <a-tab-pane
                    key="preferred-agents"
                    :tab="$t('message.toPreferredAgents')"
                    v-if="false && !isOwneredContact"
                    class="d-none"
                >
                    <div class="search-input">
                        <a-input-search
                            :placeholder="$t('message.searchPreferredAgent')"
                            v-model="preferredAgentSearchText"
                            @search="onPreferredAgentSearch"
                        />
                    </div>
                    <template v-if="filteredPreferredAgents.length > 0">
                        <SupsisTable :columns="columns" :rows="filteredPreferredAgents" v-bind="tableOptions" />
                    </template>
                    <template v-else>
                        <div class="no-agents-placeholder">
                            <a-icon type="frown" />
                            <p>{{ $t("message.noPreferredAgentsAvailableToForward") }}</p>
                        </div>
                    </template>
                </a-tab-pane>
                <a-tab-pane key="to-queue" :tab="$t('message.toQueue')" v-if="!isOwneredContact">
                    <div class="d-flex flex-column gap-2 justify-content-center align-items-center">
                        <div class="queue-info">
                            <p>
                                {{ $t("message.ifYouSendThisConversationToTheQueue") }}
                            </p>
                            <p>
                                {{ $t("message.ifAutomaticChatAssignmentIsDisabled") }}
                            </p>
                        </div>
                        <div class="queue-action">
                            <SupsisButton @click="forwardToQueue" ghost> {{ $t("message.sendQueue") }}</SupsisButton>
                        </div>
                    </div>
                </a-tab-pane>
            </a-tabs>
        </template>
        <template v-else-if="currentScreen === 'departments'">
            <ForwardDepartment
                :contact="contact"
                :selectedUserId="selectedUserId"
                @forward="onDepartmentSelected"
                @back="currentScreen = 'agents'"
                :defaultSelectedDepartment="$_.get(contact, 'lastConversation.subject')"
                :default-change-owner-ship="changeOwnerShip"
            />
        </template>
    </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapGetters, mapState } from "vuex";
import SupsisTable from "../../../../components/SupsisTable.vue";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import { sendSocketMessage } from "../../../../utils/socketStream";
import log2 from "../../../../utils/log2";
import _ from "lodash";
import ForwardDepartment from "./ForwardDepartment.vue";

const log = log2("ChatForwardWindow");
export default Vue.extend({
    name: "ChatForwardWindow",
    components: {
        SupsisTable,
        SupsisButton,
        ForwardDepartment,
    },
    props: {
        contact: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            changeOwnerShip: false,
            columns: [
                {
                    title: this.$t("message.agentName"),
                    key: "name",
                    field: "name",
                },
                {
                    title: this.$t("message.status"),
                    key: "status",
                    field: "status",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        const isOnline = row.isOnline && row.status === "Online";
                        return h(
                            "div",
                            {
                                style: {
                                    color: isOnline ? "green" : "red",
                                },
                            },
                            isOnline ? "Online" : "Offline"
                        );
                    },
                },
                {
                    title: this.$t("message.departments"),
                    key: "subjects",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h(
                            "div",
                            {},
                            this.getUserDepartmentNames(row).map((departmentName) => {
                                return h(
                                    "a-tag",
                                    {
                                        props: {
                                            color: this.getDepartmentColor(departmentName),
                                        },
                                        style: {
                                            marginRight: "4px",
                                        },
                                    },
                                    departmentName
                                );
                            })
                        );
                    },
                },
                {
                    title: this.$t("message.lastLoginPlatform"),
                    key: "lastLoginPlatform",
                    field: "lastLoginPlatform",
                },
                {
                    title: this.$t("message.actions"),
                    key: "actions",
                    width: "100px",
                    align: "center",
                    operationColumn: true,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        const isRoot = row.role.isRoot;
                        const onlyAdminsShowsNoOwnerContacts =
                            this.$store.state.setting?.settings?.onlyAdminsShowsNoOwnerContacts || false;

                        const isMeRoot = this.$store.state.auth.user?.role?.isRoot;
                        let forwardEnabled = true;
                        if (onlyAdminsShowsNoOwnerContacts && !isRoot && !this.isOwneredContact) {
                            forwardEnabled = false;
                        }
                        if (isMeRoot && !forwardEnabled) {
                            forwardEnabled = true;
                        }
                        console.log("row", {
                            row,
                            isRoot,
                            onlyAdminsShowsNoOwnerContacts,
                            forwardEnabled,
                        });
                        let tooltipContent = `Bu kişiye yetkilerden dolayı yönlendirilemez. Sahiplik ataması yaparak ilerleyebilirsiniz.`;

                        if (forwardEnabled) {
                            if (this.isOwneredContact) {
                                tooltipContent = `Hem sahiplik aktarır hemde aktif chat varsa yönlendirir`;
                            } else {
                                tooltipContent = `Direk chati yönlendirir sahiplik ataması yapmaz.`;
                            }
                        }
                        return (
                            <div>
                                {!this.isOwneredContact && (
                                    <SupsisButton
                                        onClick={() => this.forwardToAgent(row._id, true)}
                                        ghost
                                        primary
                                        v-tippy={{ followCursor: true, placement: "bottom-end" }}
                                        content={"Hem sahiplik aktarır hemde aktif chat varsa yönlendirir"}
                                    >
                                        Yönlendir & Sahiplik Ver
                                    </SupsisButton>
                                )}

                                <SupsisButton
                                    onClick={() => this.forwardToAgent(row._id, this.isOwneredContact)}
                                    ghost
                                    primary
                                    disabled={!forwardEnabled}
                                    content={tooltipContent}
                                    v-tippy={{ followCursor: true, placement: "bottom-end" }}
                                >
                                    {`${
                                        this.isOwneredContact
                                            ? "Yönlendir & Sahiplik Ver"
                                            : this.$t("message.forwardRep")
                                    }`}
                                </SupsisButton>
                            </div>
                        );
                    },
                },
            ],
            departmentColumns: [
                {
                    title: this.$t("message.departmentName"),
                    key: "name",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        const currentLocaleExists = !!row.languageContent[this.$i18n.locale];
                        const firstLocale = Object.keys(row.languageContent)[0];
                        return currentLocaleExists
                            ? row.languageContent[this.$i18n.locale].name
                            : row.languageContent[firstLocale].name;
                    },
                },
                {
                    title: this.$t("message.actions"),
                    key: "actions",
                    width: "150px",
                    align: "center",
                    operationColumn: true,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return (
                            <div>
                                <SupsisButton
                                    onClick={() => this.forwardToDepartment(row._id)}
                                    ghost
                                    primary
                                    disabled={!this.isDepartmentAllowed(row._id)}
                                >
                                    {this.$t("message.forwardToDepartment")}
                                </SupsisButton>
                            </div>
                        );
                    },
                },
            ],
            departmentColors: {},
            searchText: "",
            departmentSearchText: "",
            tableOptions: {
                rowStyleOption: {
                    stripe: true,
                },
                pagination: {
                    page: 1,
                    perPage: 10,
                    total: 0,
                    defaultPageSize: 10,
                    pageSizeOptions: [10, 20, 50],
                },
            },
            groupedAgentColumns: [
                {
                    title: this.$t("message.agentName"),
                    key: "name",
                    field: "name",
                },
                {
                    title: this.$t("message.status"),
                    key: "status",
                    field: "status",
                },
                {
                    title: this.$t("message.actions"),
                    key: "actions",
                    width: "200px",
                    align: "center",
                    operationColumn: true,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return (
                            <div>
                                <SupsisButton
                                    onClick={() => this.forwardToDepartmentAndAgent(row.departmentId, row._id)}
                                    ghost
                                    primary
                                >
                                    {this.$t("message.forwardToDepartmentAndAssign")}
                                </SupsisButton>
                            </div>
                        );
                    },
                },
            ],
            groupedSearchText: "",
            expandedDepartments: [],
            showDepartmentSelection: false,
            selectedUserId: "",
            currentScreen: "agents",
            mobileAgentSearchText: "",
            preferredAgentSearchText: "",
        };
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users.data,
            channels: (state) => state.channel.channels,
            user: (state) => state.auth.user,
            settings: (state) => state.setting.settings,
            departments: (state) => state.subject.subjects?.data || [],
            site: (state) => state.site.site,
            zohoChannelExists: (state) => !!state.channel.channels.some((channel) => channel.type === "zoho"),
        }),
        ...mapGetters("channel", ["isChannelVisibleForMe", "isChannelVisibleForUser"]),
        isOwneredContact() {
            return !!this.contact?.owner;
        },
        availableAgents() {
            const conversation = this.contact.lastConversation;
            const subject = conversation?.subject;
            const channel = conversation?.channel;
            const loggedUser = this.$store.state.auth.user;
            return this.users.filter((user) => {
                const isUserOnline = true;
                //const isMe = user._id === loggedUser._id;
                const isMe = false;
                const channelMatched = !channel || this.isChannelVisibleForUser(channel, user);
                const isValid = isUserOnline && !isMe && channelMatched;
                return isValid;
            });
        },
        filteredAgents() {
            if (!this.searchText) return this.availableAgents;
            const searchText = this.searchText.toLowerCase();
            return this.availableAgents.filter((agent) => agent.name.toLowerCase().includes(searchText));
        },
        allowedDepartments() {
            return this.getAllowedDepartments();
        },
        sortedDepartments() {
            const allowedDepartmentsIds = this.getAllowedDepartments();
            console.log("allowedDepartmentsIds", allowedDepartmentsIds);
            const allowedDepartments = this.departments.filter((dept) => allowedDepartmentsIds.includes(dept._id));
            return _.orderBy(allowedDepartments, (dept) => (this.isDepartmentAllowed(dept._id) ? 0 : 1), ["asc"]);
        },
        filteredDepartments() {
            if (!this.departmentSearchText) return this.sortedDepartments;
            const searchText = this.departmentSearchText.toLowerCase();
            return this.sortedDepartments.filter((dept) => {
                const currentLocaleExists = !!dept.languageContent[this.$i18n.locale];
                const firstLocale = Object.keys(dept.languageContent)[0];
                const departmentName = currentLocaleExists
                    ? dept.languageContent[this.$i18n.locale].name
                    : dept.languageContent[firstLocale].name;
                return departmentName.toLowerCase().includes(searchText);
            });
        },
        groupedAgents() {
            const availableAgents = this.availableAgents;
            const groupedAgents = this.allowedDepartments.map((departmentId) => {
                const department = this.departments.find((dept) => dept._id === departmentId);
                const currentLocaleExists = !!department.languageContent[this.$i18n.locale];
                const firstLocale = Object.keys(department.languageContent)[0];
                const departmentName = currentLocaleExists
                    ? department.languageContent[this.$i18n.locale].name
                    : department.languageContent[firstLocale].name;

                const agents = availableAgents.filter((agent) => {
                    const departments = agent.subjects || [];
                    return departments.includes(departmentId) || departments.length === 0;
                });

                return {
                    departmentId,
                    departmentName,
                    agents: agents.map((agent) => ({ ...agent, departmentId })),
                };
            });

            this.expandedDepartments = groupedAgents.map((group) => group.departmentId);

            return groupedAgents.filter((group) => group.agents.length > 0);
        },
        filteredGroupedAgents() {
            if (!this.groupedSearchText) return this.groupedAgents;
            const searchText = this.groupedSearchText.toLowerCase();
            return this.groupedAgents.filter((group) => {
                const departmentName = group.departmentName.toLowerCase();
                const agentNames = group.agents.map((agent) => agent.name.toLowerCase());
                return departmentName.includes(searchText) || agentNames.some((name) => name.includes(searchText));
            });
        },
        mobileAgents() {
            const conversation = this.contact.lastConversation;
            const subject = conversation?.subject;
            const channel = conversation?.channel;
            const departmentForceEnabled = !!this.settings.subject;
            const loggedUser = this.$store.state.auth.user;
            return this.users.filter((user) => {
                const isMobileUser = user.lastLoginPlatform === "mobile";
                const isMe = user._id === loggedUser._id;
                const subjectMatched =
                    !subject ||
                    (departmentForceEnabled &&
                        (user.subjects?.includes(subject) || user.subjects?.length === 0 || user.subjects === null));
                const channelMatched = !channel || this.isChannelVisibleForUser(channel, user);
                const isRootUser = user.role.isRoot;
                const ownerMatched = isRootUser || !this.contact?.owner || this.contact?.owner === user._id;
                const isValid = isMobileUser && !isMe && subjectMatched && channelMatched && ownerMatched;
                if (!isValid) {
                    console.log("mobile agent not valid to forward " + user.name, {
                        isMobileUser,
                        isMe,
                        subjectMatched,
                        channelMatched,
                        ownerMatched,
                    });
                }
                return isValid;
            });
        },
        filteredMobileAgents() {
            if (!this.mobileAgentSearchText) return this.mobileAgents;
            const searchText = this.mobileAgentSearchText.toLowerCase();
            return this.mobileAgents.filter((agent) => agent.name.toLowerCase().includes(searchText));
        },
        preferredAgents() {
            const conversation = this.contact.lastConversation;
            const subject = conversation?.subject;
            const channel = conversation?.channel;
            const departmentForceEnabled = !!this.settings.subject;
            const loggedUser = this.$store.state.auth.user;
            return this.contact.preferredAgents
                ?.filter((userId) => {
                    const user = this.users.find((u) => u._id === userId);
                    if (!user) return false;
                    const isMe = user._id === loggedUser._id;
                    const isOnline = user.isOnline && user.status === "Online";
                    const subjectMatched =
                        !subject ||
                        (departmentForceEnabled &&
                            (user.subjects?.includes(subject) ||
                                user.subjects?.length === 0 ||
                                user.subjects === null));
                    const channelMatched = !channel || this.isChannelVisibleForUser(channel, user);
                    const isRootUser = user.role.isRoot;
                    const ownerMatched = isRootUser || !this.contact?.owner || this.contact?.owner === user._id;
                    const isValid = !isMe && isOnline && subjectMatched && channelMatched && ownerMatched;
                    if (!isValid) {
                        console.log("preferred agent not valid to forward " + user.name, {
                            isMe,
                            isOnline,
                            subjectMatched,
                            channelMatched,
                            ownerMatched,
                        });
                    }
                    return isValid;
                })
                .map((userId) => this.users.find((u) => u._id === userId));
        },
        filteredPreferredAgents() {
            if (!this.preferredAgentSearchText) return this.preferredAgents;
            const searchText = this.preferredAgentSearchText.toLowerCase();
            return this.preferredAgents.filter((agent) => agent.name.toLowerCase().includes(searchText));
        },
    },
    methods: {
        ...mapActions({
            leaveContactConversation: "chat/leaveContactConversation",
        }),
        getAllowedDepartments() {
            let channel = this.contact?.channel;
            if (!channel) return this.departments.map((dept) => dept._id);

            channel = typeof channel === "string" ? this.channels.find((c) => c._id === channel) : channel;

            let { users, departments } = channel?.visibility || { users: [], departments: [] };
            users = Array.isArray(users) ? users : [];
            departments = Array.isArray(departments) ? departments : [];
            if (users.length === 0 && departments.length === 0) {
                return this.departments.map((dept) => dept._id);
            }

            const userDepartments = users.length
                ? _.uniq(
                      users.flatMap((userId) => {
                          const user = this.users.find((u) => u._id === userId);
                          return user?.subjects || [];
                      })
                  )
                : [];

            return _.uniq([...departments, ...userDepartments]);
        },
        forwardToAgent(userId, changeOwnerShip = false) {
            this.selectedUserId = userId;
            this.currentScreen = "departments";
            this.changeOwnerShip = changeOwnerShip;
        },
        onDepartmentSelected(departmentId) {
            this.showDepartmentSelection = false;

            const contact = this.contact;
            if (contact.lastConversation) {
                const conversationId = contact.lastConversation._id;
                log("forwarding agent", this.selectedUserId, {
                    conversation: conversationId,
                    contact: contact,
                });

                this.leaveContactConversation(contact);

                const isOwneredContact = this.isOwneredContact;
                const changeOwnerShip = this.changeOwnerShip;
                if (isOwneredContact && !changeOwnerShip) {
                    this.$confirm({
                        title: "Sahiplik Transferi",
                        content:
                            "Bu kişinin sahibi vardır. Yönlendirmek için sahipliğide transfer etmeniz gerekmektedir. Kişinin sahipliğinide transfer etmek istediğinize emin misiniz?",
                        onOk: () => {
                            this.sendForwardConversation(conversationId, departmentId, true);
                        },
                        onCancel: () => {
                            // Do nothing
                        },
                    });
                } else {
                    this.sendForwardConversation(conversationId, departmentId, changeOwnerShip);
                }
            }
        },
        sendForwardConversation(conversationId, departmentId, changeOwnership) {
            console.log("sendForwardConversation", { conversationId, departmentId, changeOwnership });
            this.$store.dispatch("chat/removeManualChatStartedContactIds", [this.contact._id]);
            this.leaveContactConversation(this.contact);
            const payload = {
                conversationId,
                userId: this.selectedUserId,
                siteId: this.site._id,
                forward: {
                    to: this.selectedUserId,
                },
                type: "forward",
                forwarderUserId: this.user?._id,
                changeOwnership,
                changeDepartmentId: departmentId,
                visitorId: this.contact._id,
            };
            if (!this.zohoChannelExists) {
                setTimeout(() => {
                    sendSocketMessage("user add-user-in-conversation", payload);
                }, 500);
                this.$emit("close");
            } else {
                if (changeOwnership) {
                    this.$confirm({
                        title: "Zoho CRM",
                        content: "Sahiplik transferini otomaik ZOHO CRM'de de yapılmasını istiyor musunuz?",
                        cancelText: "Hayır, sadece SUPSIS'te yap",
                        okText: "Evet,Zoho CRM'de de yap",
                        width: "500px",
                        onOk: () => {
                            setTimeout(() => {
                                payload.publishToZoho = true;
                                sendSocketMessage("user add-user-in-conversation", payload);
                            }, 500);
                            this.$emit("close");
                        },
                        onCancel: () => {
                            setTimeout(() => {
                                sendSocketMessage("user add-user-in-conversation", payload);
                            }, 500);
                            this.$emit("close");
                        },
                    });
                } else {
                    setTimeout(() => {
                        sendSocketMessage("user add-user-in-conversation", payload);
                    }, 500);
                    this.$emit("close");
                }
            }
        },
        getUserDepartmentNames(user) {
            return (user.subjects || []).length
                ? this.departments
                      .filter((x) => (user.subjects || []).includes(x._id))
                      .map((x) => {
                          const currentLocaleExists = !!x.languageContent[this.$i18n.locale];
                          const firstLocale = Object.keys(x.languageContent)[0];
                          return currentLocaleExists
                              ? x.languageContent[this.$i18n.locale].name
                              : x.languageContent[firstLocale].name;
                      })
                : [this.$t("message.allDepartments")];
        },
        getUserDepartmentColor(department) {
            if (!this.departmentColors[department]) {
                const colors = ["#1E90FF", "#32CD32", "#FF8C00", "#8A2BE2", "#DC143C"];
                const index = Object.keys(this.departmentColors).length % colors.length;
                this.departmentColors[department] = colors[index];
            }
            return this.departmentColors[department];
        },
        getDepartmentColor(department) {
            if (!this.departmentColors[department]) {
                const colors = ["#1E90FF", "#32CD32", "#FF8C00", "#8A2BE2", "#DC143C"];
                const index = Object.keys(this.departmentColors).length % colors.length;
                this.departmentColors[department] = colors[index];
            }
            return this.departmentColors[department];
        },
        onSearch() {
            this.tableOptions.pagination.page = 1;
        },
        isDepartmentAllowed(departmentId) {
            console.log("isDepartmentAllowed", departmentId, this.allowedDepartments);
            return (
                this.allowedDepartments.includes(departmentId) &&
                this.contact.lastConversation?.subject !== departmentId
            );
        },
        forwardToDepartment(departmentId) {
            console.log("forwardToDepartment", departmentId);
            const contact = this.contact;
            const userId = this.user?._id;
            if (contact.lastConversation) {
                const conversationId = contact.lastConversation._id;
                log("forwarding department ", {
                    conversation: conversationId,
                    contact: contact,
                    department: departmentId,
                });

                this.leaveContactConversation(contact);
                setTimeout(() => {
                    sendSocketMessage("user forward-conversation-to-department", {
                        conversationId,
                        forwarderUserId: userId,
                        siteId: this.site._id,
                        departmentId,
                        contactId: contact._id,
                        visitorId: contact._id,
                    });
                }, 500);
                this.$emit("close");
            }
        },
        onDepartmentSearch() {
            this.tableOptions.pagination.page = 1;
        },
        forwardToDepartmentAndAgent(departmentId, userId) {
            console.log("forwardToDepartmentAndAgent", departmentId, userId);
            const contact = this.contact;
            const forwarderUserId = this.user?._id;
            if (contact.lastConversation) {
                const conversationId = contact.lastConversation._id;
                log("forwarding department and agent", {
                    conversation: conversationId,
                    contact: contact,
                    department: departmentId,
                    agent: userId,
                });

                this.leaveContactConversation(contact);
                setTimeout(() => {
                    sendSocketMessage("user forward-conversation-to-department-agent", {
                        conversationId,
                        forwarderUserId,
                        siteId: this.site._id,
                        departmentId,
                        userId,
                        contactId: contact._id,
                        visitorId: contact._id,
                    });
                }, 500);
                this.$emit("close");
            }
        },
        onGroupedSearch() {
            this.tableOptions.pagination.page = 1;
        },
        toggleDepartment(departmentId) {
            const index = this.expandedDepartments.indexOf(departmentId);
            if (index > -1) {
                this.expandedDepartments.splice(index, 1);
            } else {
                this.expandedDepartments.push(departmentId);
            }
        },
        isDepartmentExpanded(departmentId) {
            return this.expandedDepartments.includes(departmentId);
        },
        onMobileAgentSearch() {
            this.tableOptions.pagination.page = 1;
        },
        onPreferredAgentSearch() {
            this.tableOptions.pagination.page = 1;
        },

        forwardToQueue() {
            const contact = this.contact;
            if (contact.lastConversation) {
                const conversationId = contact.lastConversation._id;
                log("forwarding to queue", {
                    conversation: conversationId,
                    contact: contact,
                });

                this.leaveContactConversation(contact);
                setTimeout(() => {
                    sendSocketMessage("user forward-to-queue", {
                        siteId: this.site._id,
                        contactId: contact._id,
                        visitorId: contact._id,
                        conversationId,
                    });
                }, 500);
                this.$emit("close");
            }
        },
        goToChannelSettings() {
            const _channel = this.contact?.channel;
            const channelId = typeof _channel === "string" ? _channel : _channel._id;
            const channel = this.channels.find((c) => c._id === channelId);
            if (channel) {
                this.$router.push(`/integrations/${channel.type}/${channel._id}/detail/visibility`);
            }
        },
    },
});
</script>

<style scoped lang="scss">
.search-input {
    margin-bottom: 16px;
}

.no-agents-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    font-size: 16px;
    color: #999;

    .anticon {
        font-size: 48px;
        margin-bottom: 16px;
    }
}

.no-departments-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    font-size: 16px;
    color: #999;

    .anticon {
        font-size: 48px;
        margin-bottom: 16px;
    }
}

.department-header {
    display: flex;
    align-items: center;
    cursor: pointer;
    margin-bottom: 8px;

    .anticon {
        margin-right: 8px;
    }

    h3 {
        margin: 0;
    }
}

.queue-info {
    margin-bottom: 16px;
    background-color: #e6f7ff;
    border: 1px solid #91d5ff;
    border-radius: 4px;
    padding: 16px;
    color: #1890ff;

    p {
        margin-bottom: 8px;

        &:last-child {
            margin-bottom: 0;
        }
    }
}

.queue-action {
    text-align: right;
}

:deep(.ant-tabs-left) {
    .ant-tabs-nav {
        min-width: 200px;
    }

    .ant-tabs-tab {
        text-align: left;
        margin: 0;
        padding: 12px 16px;
    }
}

:deep(.ant-tabs-content) {
    padding-left: 24px;
}

.channel-visibility-info {
    margin-bottom: 16px;

    .info-box {
        background-color: #e6f7ff;
        border: 1px solid #91d5ff;
        border-radius: 4px;
        padding: 16px;
    }

    .info-content {
        display: flex;
        align-items: flex-start;
    }

    .info-icon {
        color: #1890ff;
        font-size: 16px;
        margin-right: 12px;
        margin-top: 2px;
    }

    .info-text {
        flex: 1;
    }

    .info-title {
        color: #1890ff;
        font-weight: 500;
        margin-bottom: 8px;
    }

    .info-description {
        color: rgba(0, 0, 0, 0.65);

        span {
            margin-right: 16px;
        }
    }
}
</style>
